import logger from '../../log';

const log = logger.getLogger(__filename);

export const REPRESENTATION = 'Representation';
export const ADAPTATION_SET = 'AdaptationSet';
export const CONTENT_TYPE = 'contentType';
export const MIME_TYPE = 'mimeType';
export const BANDWIDTH = 'bandwidth';

/**
 * Returns true if the adaptationSet is the desired contentType.
 * If content type does not exist the mime type is checked for inclusion of the contentType
 * @param adaptationSet - the AdaptationSet Element
 * @param contentType - the contentType to text against.
 * @returns true if the adaptationSet is the desired contentType
 */
export function shouldStripAdaptationSet(
  adaptationSet: Element,
  contentType: string
): boolean {
  const contentTypeValue = adaptationSet.getAttribute(CONTENT_TYPE);
  if (!contentType || !contentTypeValue) {
    const mimeTypeValue = adaptationSet.getAttribute(MIME_TYPE);
    return !!mimeTypeValue && mimeTypeValue.includes(contentType);
  }
  return contentTypeValue == contentType;
}

export function getDesiredRepresentationsAndParent(
  document: Document,
  contentType: string
): [HTMLCollection, Element] | [] {
  const adaptationSets = document.getElementsByTagName(ADAPTATION_SET);
  for (const adaptationSet of adaptationSets) {
    if (shouldStripAdaptationSet(adaptationSet, contentType)) {
      return [
        adaptationSet.getElementsByTagName(REPRESENTATION),
        adaptationSet
      ];
    }
  }
  return [];
}

export default function (
  document: Document,
  contentType: string,
  minBitrate: number,
  maxBitrate: number
): Document {
  const [representations, parent] = getDesiredRepresentationsAndParent(
    document,
    contentType
  );
  if (parent && representations) {
    const toRemove = [];
    const toKeep = [];
    let lowest: [number, Element] = [Infinity, representations[0]];
    let highestUnderMin: [number, Element] = [
      Number.NEGATIVE_INFINITY,
      representations[0]
    ];
    const originalBandwidths = [];
    const selectedBandwidhs = [];
    for (const representation of representations) {
      const bandwidthValue = representation.getAttribute(BANDWIDTH);
      if (!bandwidthValue) {
        // no value,
        // TODO ? toRemove.push(representation);
        continue;
      }
      // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/parseInt#Description
      const bandwidth = parseInt(bandwidthValue, 10);
      originalBandwidths.push(bandwidth);
      if (bandwidth < minBitrate || bandwidth > maxBitrate) {
        toRemove.push(representation);
      } else {
        toKeep.push(representation);
        selectedBandwidhs.push(bandwidth);
      }
      // lowest
      const [mb] = lowest;
      if (mb > bandwidth) {
        lowest = [bandwidth, representation];
      }
      // highest under minimum bandwidth
      const [br] = highestUnderMin;
      if (bandwidth < minBitrate && br < bandwidth) {
        highestUnderMin = [bandwidth, representation];
      }
    }
    // remove unused bitrates
    for (const representation of toRemove) {
      parent.removeChild(representation);
    }
    // if we don't find any, add the smallest back in
    // this also means all representations were removed.
    // so we need to add the smallest back in.
    if (toKeep.length == 0) {
      const [lbr, lel] = lowest;
      const [hbr, hel] = highestUnderMin;
      if (lbr > hbr) {
        selectedBandwidhs.push(lbr);
        parent.appendChild(lel);
      } else {
        selectedBandwidhs.push(hbr);
        parent.appendChild(hel);
      }
    }
    log.debug(`Original ${contentType} bitrates: `, originalBandwidths);
    log.debug(`Selected ${contentType} bitrates: `, selectedBandwidhs);
  } else {
    log.info('Unable to filter bitrates', { parent, representations });
  }
  return document;
}
